// src/components/TermsGate.js
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { logOrgActivity } from "../utils/orgAudit";

export default function TermsGate({
  version = "v1",
  policy = "perUser",        // "perUser" | "global" | "everyLogin"
  showBeforeLogin = false,   // false = only after login
  allowSkip = false,
}) {
  const { user, activeOrgId } = useAuth();
  const username = user?.username || user?.email || user?.uid || "";

  const BASE = `nia_terms_${version}`;
  const perUserKey   = username ? `${BASE}__${username}` : null;
  const marketingKey = username ? `${BASE}_mkt__${username}` : `${BASE}_mkt__anon`;
  const stampKey     = `${BASE}_ts__${username || "anon"}`;

  // Migration: if you previously stored a global acceptance, ignore it in per-user mode.
  React.useEffect(() => {
    if (policy === "perUser") {
      try { localStorage.removeItem(BASE); } catch {}
    }
  }, [policy, BASE]);

  // Determine acceptance (no early return yet; just compute values)
  const accepted = React.useMemo(() => {
    try {
      if (policy === "everyLogin") return false;
      if (policy === "global")     return localStorage.getItem(BASE) === "1";
      if (!perUserKey) return false;
      return localStorage.getItem(perUserKey) === "1";
    } catch {
      return false;
    }
  }, [policy, BASE, perUserKey]);

  // Local UI state (declared unconditionally)
  const [c1, setC1] = React.useState(false);
  const [c2, setC2] = React.useState(false);
  const [c3, setC3] = React.useState(false);
  const [c4, setC4] = React.useState(false);
  const [c5, setC5] = React.useState(false);
  const [c6, setC6] = React.useState(false); // optional marketing
  const [dismissed, setDismissed] = React.useState(false);
  const [snoozed] = React.useState(false);

  const allRequired = c1 && c2 && c3 && c4 && c5;
  const masterRequiredChecked = allRequired;

  const toggleMasterRequired = (checked) => {
    setC1(checked);
    setC2(checked);
    setC3(checked);
    setC4(checked);
    setC5(checked);
    // c6 is optional; leave it as-is
  };

  const accept = () => {
    if (!allRequired) return;
    try {
      if (policy === "global") {
        localStorage.setItem(BASE, "1");
      } else if (policy === "perUser" && perUserKey) {
        localStorage.setItem(perUserKey, "1");
      }
      localStorage.setItem(marketingKey, c6 ? "1" : "0");
      localStorage.setItem(stampKey, String(Date.now()));
    } catch {}
    if (user?.uid) {
      const orgId = activeOrgId || user?.orgId || "org_default";
      logOrgActivity(user, orgId, "consent_accept", {
        version,
        marketingOptIn: c6 ? "yes" : "no",
        acceptedAt: Date.now(),
      });
    }
    setDismissed(true);
    window.dispatchEvent(new Event("terms-accepted"));
  };

  const decline = () => {
    try { localStorage.clear(); } catch {}
    window.location.reload();
  };

  // Decide whether to render (do it here, AFTER hooks are declared)
  const shouldShow = !dismissed && !accepted && !snoozed && (showBeforeLogin || !!username);
  if (!shouldShow) return null;

  return (
    <div className="nia-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="tos-title">
      <div className="nia-modal">
        <div className="nia-modal-header">
          <h2 id="tos-title" style={{ margin: 0 }}>Before you continue</h2>
          <p style={{ margin: "6px 0 0", color: "var(--muted)" }}>
            Please review and confirm the following to use Nia COACH.
          </p>
        </div>

        <div className="nia-modal-body">
          {/* Master: agree to all required */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              border: "1px solid var(--border)",
              borderRadius: 10,
              background: "var(--card)",
              marginBottom: 10
            }}
          >
            <input
              type="checkbox"
              checked={masterRequiredChecked}
              onChange={(e) => toggleMasterRequired(e.target.checked)}
            />
            <strong>I agree to all required statements</strong>
          </label>

          <ul className="nia-modal-list">
            <li>
              <input type="checkbox" checked={c1} onChange={e=>setC1(e.target.checked)} />
              <span>I am 16+ and using Nia COACH voluntarily.</span>
            </li>
            <li>
              <input type="checkbox" checked={c2} onChange={e=>setC2(e.target.checked)} />
              <span>
                I understand Nia COACH provides coaching guidance only and is not a substitute
                for therapy, diagnosis, or medical care.
              </span>
            </li>
            <li>
              <input type="checkbox" checked={c3} onChange={e=>setC3(e.target.checked)} />
              <span>
                I will not use Nia COACH for emergencies and will seek immediate help from local
                services or a licensed professional when needed.
              </span>
            </li>
            <li>
              <input type="checkbox" checked={c4} onChange={e=>setC4(e.target.checked)} />
              <span>
                I consent to processing and storage of my inputs to deliver features and
                improve service quality (see Privacy Notice).
              </span>
            </li>
            <li>
              <input type="checkbox" checked={c5} onChange={e=>setC5(e.target.checked)} />
              <span>I accept the Terms of Service and Privacy Notice.</span>
            </li>
            <li>
              <input type="checkbox" checked={c6} onChange={e=>setC6(e.target.checked)} />
              <span>(Optional) Send me updates about new features and programs.</span>
            </li>
          </ul>

          <div style={{ marginTop: 14, fontSize: 12, color: "var(--muted)" }}>
            Read our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
          </div>
        </div>

        <div className="nia-modal-footer">
          <button className="btn btn-ghost" onClick={decline}>Decline & Close</button>
          <button className="btn btn-primary" onClick={accept} disabled={!allRequired}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
